import { useStore } from '@nanostores/react';
import { PricingCardActionButton } from './Action';
import { PricingCardBase } from './PricingCardBase';
import { PricingSelector } from './PricingSelector';
import { getProPlansPerks } from '~/lib/stores/pricing/config';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { proPricingPlansStore } from '~/lib/stores/pricing/proPlans';
import { userStore } from '~/lib/stores/user';

interface Props {
  isPersonalWorkspace: boolean;
  tokenStatsMessage: string | null;
}

export const ProPlansCard = ({ isPersonalWorkspace, tokenStatsMessage }: Props) => {
  const loggedIn = useStore(userStore.loggedIn);
  const selectedInterval = useStore(pricingStore.selectedInterval);

  const actions = useStore(proPricingPlansStore.actions);
  const messages = useStore(proPricingPlansStore.messages);
  const dropdownOptions = useStore(proPricingPlansStore.dropdownOptions);
  const selectedPricing = useStore(proPricingPlansStore.selectedPricing);
  const activePricing = useStore(proPricingPlansStore.activePricing);
  const membership = useStore(proPricingPlansStore.membership);

  const pricingBeingRequested = useStore(pricingStore.pricingBeingRequested);
  const isLoading = useStore(pricingStore.isLoading);

  const shouldDisplayTokenStatsMessage = selectedPricing?.tier === activePricing?.tier;
  const hasSubscription = (membership?.tier ?? 0) > 0;

  const shouldDisplayExactTierTokens =
    isPersonalWorkspace && (hasSubscription || (!hasSubscription && selectedPricing.tier > 1));

  return (
    <PricingCardBase
      title={
        <div className="flex items-center justify-between gap-2">
          <span>Pro</span>
          <span className="flex items-center justify-center px-2 h-6 rounded-full bg-bolt-ds-inverseSurface text-bolt-ds-inverseOnSurface text-xs font-medium uppercase">
            Popular
          </span>
        </div>
      }
      price={selectedInterval === 'yearly' ? selectedPricing?.rateMonthly.year : selectedPricing?.rateMonthly.month}
      billedLabel={`billed ${selectedInterval}`}
      priceLabel="per month"
      selector={
        loggedIn &&
        isPersonalWorkspace && (
          <PricingSelector
            options={dropdownOptions}
            label="Monthly tokens"
            selectedPricing={selectedPricing}
            onSelectPricing={(pricing) => proPricingPlansStore.selectPricing(pricing)}
          />
        )
      }
      perks={{
        label: 'You get:',
        items: getProPlansPerks({
          shouldDisplayExactTierTokens,
          pricing: selectedPricing,
        }),
      }}
      highlighted
      messages={
        tokenStatsMessage && shouldDisplayTokenStatsMessage
          ? [
              {
                text: tokenStatsMessage,
                placement: 'under-selector',
              },
              ...messages,
            ]
          : messages
      }
    >
      {actions.map((action) => (
        <PricingCardActionButton
          key={action.id}
          action={action}
          loading={pricingBeingRequested?.type === 'pro' && pricingBeingRequested.id === action.id}
          disabled={isLoading}
        />
      ))}
    </PricingCardBase>
  );
};
